import { useState } from 'react';
import {
  changePassword,
  login,
  logout,
  register,
  resendOtp,
  resendOtpPassword,
  sendOtp,
  sendOtpPassword,
  verifyOtp,
  verifyOtpPassword,
} from '../../services/authService/authService';
import useCommonFunctions from '../../hooks/useCommonFunctions';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import PaymentController from '../PaymentController.js/PaymentController';
import { trackCompleteRegistration } from '../../utils/gtm';

const AuthController = () => {
  const { t } = useTranslation();
  const { goTo } = useCommonFunctions();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [agree, setAgree] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { userHaveSubscription } = PaymentController();

  const clearAuthStorageAndReload = () => {
    localStorage.removeItem('auth');
    localStorage.removeItem('haveSubscription');
    window.location.reload();
  };

  const shouldForceLogout = (message) => {
    return message === 'Unauthorized,' || message === 'notFound';
  };

  const handleAuthErrorToast = (message) => {
    if (!message) return;

    toast.error(t(message), {
      onClose: () => {
        if (shouldForceLogout(message)) {
          clearAuthStorageAndReload();
        }
      },
    });
  };

  const handleSplitErrorToast = (message) => {
    if (!message) return;

    const firstWord = message.split(' ')[0];

    toast.error(t(firstWord), {
      onClose: () => {
        if (firstWord === 'Unauthorized,' || message === 'notFound') {
          clearAuthStorageAndReload();
        }
      },
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      const userData = await login(email, password);

      if (!userData?.id) {
        setErrorMessage(t('loginFailed'));
        return;
      }

      localStorage.setItem('auth', userData.id);

      const response = await userHaveSubscription();

      if (response) {
        if (response.active && response.hasActiveSubscription) {
          localStorage.setItem('haveSubscription', 'true');
          goTo('/AlreadyHaveAccount');
        } else {
          goTo('/plans');
        }
      }
    } catch (error) {
      setErrorMessage(error?.message || t('somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      const userData = await register(name, email, password, repeatPassword);

      if (!userData?.id) {
        setErrorMessage(t('registerFailed'));
        return;
      }

      localStorage.setItem('auth', userData.id);

      await sendOtp(email);

      goTo('/otp', { state: { email } });
    } catch (error) {
      setErrorMessage(error?.message || t('somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogOut = async () => {
    setLoading(true);

    try {
      await logout();
      localStorage.removeItem('haveSubscription');
      goTo('/');
    } catch (error) {
      handleSplitErrorToast(error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    setLoading(true);

    try {
      await sendOtp(email);
    } catch (error) {
      handleAuthErrorToast(error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async (callbackFnc) => {
    setLoading(true);

    try {
      await resendOtp(email);
      if (callbackFnc) callbackFnc();
    } catch (error) {
      handleAuthErrorToast(error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (otp, callbackFnc) => {
    setLoading(true);

    try {
      await verifyOtp(email, otp);

      trackCompleteRegistration({
        method: 'email',
      });

      if (callbackFnc) {
        callbackFnc();
      } else {
        goTo('/plans');
      }
    } catch (error) {
      handleAuthErrorToast(error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtpPassword = async () => {
    setLoading(true);

    try {
      const response = await sendOtpPassword(email);

      if (response) {
        goTo('/confirmOtpPassword', { state: { email } });
      }
    } catch (error) {
      handleAuthErrorToast(error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtpPassword = async (callbackFnc) => {
    setLoading(true);

    try {
      await resendOtpPassword(email);
      if (callbackFnc) callbackFnc();
    } catch (error) {
      handleAuthErrorToast(error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtpPassword = async (otp) => {
    setLoading(true);

    try {
      const response = await verifyOtpPassword(email, otp);

      if (response) {
        goTo('/changePassword', { state: { email, otp } });
      }
    } catch (error) {
      handleAuthErrorToast(error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (otp) => {
    setLoading(true);

    try {
      const response = await changePassword(
        email,
        otp,
        password,
        repeatPassword
      );

      if (response) {
        toast.success(t('passwordChanged'), {
          onClose: () => {
            goTo('/');
          },
        });
      }
    } catch (error) {
      handleAuthErrorToast(error?.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    name,
    email,
    password,
    errorMessage,
    loading,
    agree,
    repeatPassword,
    setName,
    setEmail,
    setPassword,
    setErrorMessage,
    setLoading,
    setAgree,
    setRepeatPassword,
    handleLogin,
    handleRegister,
    handleLogOut,
    handleSendOtp,
    handleResendOtp,
    handleVerifyOtp,
    handleSendOtpPassword,
    handleResendOtpPassword,
    handleVerifyOtpPassword,
    handleChangePassword,
    goTo,
  };
};

export default AuthController;